import {
  createSlice,
  PayloadAction,
  Draft,
  createSelector
} from '@reduxjs/toolkit';
import {userApi} from '../api/userApi';
import {
  UserDetails,
  AdminStoreRoles,
  CombinedStoreRoles,
  AdminRoles,
  CombinedRoles
} from '../models/user';
import type {RootState} from '../store';

export interface UserState {
  token: string | null;
  loginStatus: 'idle' | 'loading' | 'failed';
  email: string | null;
  role: CombinedRoles | null;
  storeRoles?: CombinedStoreRoles[] | null;
}

const initialState: UserState = {
  token: null,
  loginStatus: 'idle',
  email: null,
  role: null,
  storeRoles: null
};

const handleLoading = (state: Draft<UserState>) => {
  state.loginStatus = 'loading';
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    clearToken: state => {
      state.token = null;
    },
    setEmail: (
      state,
      action: PayloadAction<{
        email: string;
        role: CombinedRoles;
        storeRole: CombinedStoreRoles[];
      }>
    ) => {
      state.email = action.payload.email;
      state.role = action.payload.role;
      state.storeRoles = action.payload.storeRole;
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(userApi.endpoints.fetchUser.matchPending, handleLoading)
      .addMatcher(
        userApi.endpoints.fetchUser.matchFulfilled,
        (state, {payload}) => {
          state.loginStatus = 'idle';
          state.email = payload.email;
          state.role = payload.role;
          state.storeRoles = payload.storeRoles;
        }
      )
      .addMatcher(userApi.endpoints.logoutUser.matchPending, handleLoading)
      .addMatcher(userApi.endpoints.logoutUser.matchFulfilled, state => {
        state.loginStatus = 'idle';
        state.token = null;
      });
  }
});

// Selectors

export const selectLoggedIn = createSelector(
  (state: RootState) => state.user,
  (user: UserDetails): boolean => user.email !== null
);

export const selectAdmin = createSelector(
  (state: RootState) => state.user,
  (user: UserDetails): boolean =>
    Object.values(AdminRoles).includes(user.role as AdminRoles)
);
export const selectAuthorized = createSelector(
  (state: RootState) => state.user,
  (user: UserDetails): boolean =>
    user.storeRoles?.some(role =>
      Object.values(AdminStoreRoles).includes(role as AdminStoreRoles)
    )
);

export const {setToken, clearToken, setEmail} = userSlice.actions;

export const {reducer} = userSlice;
